/**
 * DT Custom Product Options: Option Property Builder
 *
 * DOM-independent utility to build _dt_product_options JSON entries.
 *
 * SCOPE 
 *   - static-production-attribute 
 *   - textfield / textfield-number (with customer-entered value)
 *
 */


(function (global) {

  // Helper: remove keys with null/undefined/empty-string values from an object 
  function removeEmptyKeys(obj) {
    if (obj && typeof obj === 'object') {
      Object.keys(obj).forEach((key) => {
        if (obj[key] === null || obj[key] === undefined || obj[key] === '') {
          delete obj[key];
        }
      });
    }
    return obj;
  }

  function assertInput(input, fnName) {
    if (!input || typeof input !== 'object') {
      throw new Error(`DTOptionPropertyBuilder.${fnName}: input object is required`);
    }
  }

  // Final Clean-Up for property object 
  function finalizeProperty(property) {
    removeEmptyKeys(property);
    removeEmptyKeys(property.value);
    return property;
  }

  /**
   * Builds a _dt_product_options entry for a static production attribute
   * @param {Object} input - raw attribute data
   * @returns {Object} property object matching the _dt_product_options schema
   */
  function buildStaticProductionAttribute(input) {
    assertInput(input, 'buildStaticProductionAttribute');

    const { handle, legacyId, attributeType, value, currency = 'EUR' } = input;

    return finalizeProperty({
      handle,
      type: 'static-production-attribute',
      currency,
      value: {
        handle,
        label: value,
        productionAttrId: legacyId,
        productionAttrType: attributeType,
        productionAttrValue: value,
        value,
      },
    });
  }

  /**
   * Builds a _dt_product_options entry for a customer-entered textfield
   * (e.g. a greeting card message, or a paid cake inscription).
   * @param {Object} input - raw textfield definition + customer text
   * @returns {Object} property object matching the _dt_product_options schema
   */
  function buildTextfieldOption(input) {
    assertInput(input, 'buildTextfieldOption');
    if (input.type !== 'textfield' && input.type !== 'textfield-number') {
      throw new Error(
        `DTOptionPropertyBuilder.buildTextfieldOption: type must be 'textfield' or 'textfield-number', got "${input.type}"`
      );
    }

    const { handle, type, displayName, productionLabel, text, price, currency = 'EUR' } = input;

    return finalizeProperty({
      handle,
      productionLabel,
      type,
      price,
      currency,
      displayName,
      value: {
        handle,
        price,
        text,
      },
    });
  }

  /**
   * @param {string} productHandle
   * @param {string[]} customerTexts - customer-entered text values, one per
   *   textfield option, in the same order as they appear on the product.
   *   Pass an empty array (or omit) if the product has no textfield options.
   * @returns {Promise<Object[]>} finished _dt_product_options array
   */
  async function buildAllProductOptions(productHandle, customerTexts = []) {
    if (!productHandle || typeof productHandle !== 'string') {
      throw new Error('DTOptionPropertyBuilder.buildAllProductOptions: productHandle (string) is required');
    }

    const html = await fetch(`/products/${productHandle}?section_id=dt-product-raw-options-data`).then((r) =>
      r.text()
    );
    const raw = JSON.parse(html.replace(/<[^>]+>/g, '').trim());

    if (raw.textfield_options.length !== customerTexts.length) {
      throw new Error(
        `DTOptionPropertyBuilder.buildAllProductOptions: product "${productHandle}" has ` +
          `${raw.textfield_options.length} textfield option(s), but ${customerTexts.length} customerTexts ` +
          `were provided. These must match 1:1, in order.`
      );
    }

    const staticEntries = raw.production_attributes.map((attr) =>
      buildStaticProductionAttribute({ ...attr, currency: raw.currency })
    );

    const textEntries = raw.textfield_options.map((def, index) =>
      buildTextfieldOption({
        ...def,
        text: customerTexts[index],
        currency: raw.currency,
        price: 0, // default to 0 
      })
    );

    return [...staticEntries, ...textEntries];
  }

  global.DTOptionPropertyBuilder = {
    buildStaticProductionAttribute,
    buildTextfieldOption,
    buildAllProductOptions,
  };
})(window);